import { t } from '../../i18n';
import { ArrowUpRight } from 'lucide-react';
import { PortfolioItem } from '../../context/PortfolioContext';
import { getImageUrl } from '../../utils/imageUtils';

interface PortfolioCardProps {
  project: PortfolioItem;
  clickable?: boolean;
}

const PortfolioCard = ({ project, clickable = false }: PortfolioCardProps) => {
  const { title, description, image, technologies, category } = project;

  return (
    <div
      className={`group relative card overflow-hidden h-full ${
        clickable ? 'cursor-pointer card-hover' : ''
      }`}
    >
      {/* Gradient border effect on hover */}
      <div className="absolute inset-0 rounded-xl p-px opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgb(var(--color-primary) / 0.5), transparent 50%, rgb(var(--color-accent) / 0.3))',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }}
      />

      {/* Image */}
      <div className="relative aspect-video overflow-hidden bg-card">
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent z-10" />

        {/* Corner accent */}
        <div
          className="absolute top-0 right-0 z-10 opacity-0 group-hover:opacity-100 transition-all duration-500"
          style={{
            width: 0,
            height: 0,
            borderStyle: 'solid',
            borderWidth: '0 40px 40px 0',
            borderColor: 'transparent rgb(var(--color-primary) / 0.3) transparent transparent',
          }}
        />

        <img
          src={getImageUrl(image)}
          alt={title}
          className="w-full h-full object-cover object-center transition-all duration-700 ease-out-expo group-hover:scale-105 img-grayscale"
        />

        {/* View project indicator */}
        {clickable && (
          <div className="absolute bottom-4 left-4 z-20 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
            <span className="flex items-center gap-2 text-xs uppercase tracking-widest text-foreground font-heading bg-background/80 backdrop-blur-sm px-3 py-2 rounded">
              {t("View Project")}
              <ArrowUpRight className="w-3 h-3" />
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="relative p-5 space-y-4">
        {/* Header */}
        <div className="flex justify-between items-start gap-3">
          <h3 className="font-heading font-bold text-lg text-foreground group-hover:text-primary transition-colors duration-300 line-clamp-2">
            {title}
          </h3>
          {clickable && (
            <span className="text-primary shrink-0 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
              <ArrowUpRight className="w-5 h-5" />
            </span>
          )}
        </div>

        {/* Category */}
        <span className="inline-block text-[10px] uppercase tracking-widest text-primary font-heading px-2 py-1 bg-primary/10 rounded">
          {category}
        </span>

        {/* Description */}
        <p className="text-muted text-sm line-clamp-2 leading-relaxed">{description}</p>

        {/* Technologies */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-foreground/5">
          {technologies.slice(0, 3).map((tech, index) => (
            <span
              key={index}
              className="text-xs text-muted/80 font-heading tracking-wide"
            >
              {tech}
              {index < Math.min(technologies.length, 3) - 1 && (
                <span className="ml-2 text-primary/50">/</span>
              )}
            </span>
          ))}
          {technologies.length > 3 && (
            <span className="text-xs text-muted/50">
              +{technologies.length - 3}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortfolioCard;
